import React, { Component } from "react";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { utcToLocal } from "../../utils/dateConvert";

class AssetTable extends Component {

  renderSortHeader = (label, sortKey) => {
    const { onSort, sortDirection, sortKey: activeSortKey } = this.props;
    const active = activeSortKey === sortKey;
    const iconClass = active && sortDirection === "desc"
      ? "fa fa-sort-down"
      : active
        ? "fa fa-sort-up"
        : "fa fa-sort";

    return (
      <button
        className={`asset-sort-button ${active ? "active" : ""}`}
        onClick={() => onSort(sortKey)}
        type="button"
      >
        {label}
        <i className={iconClass}></i>
      </button>
    );
  };

  renderFeature = (enabled, iconClass, label) => {
    if (!enabled) {
      return null;
    }

    return (
      <span className="asset-feature" title={label}>
        <i className={iconClass}></i>
      </span>
    );
  };

  normalizeIconClass = (icon) => {
    const value = String(icon || "").trim();
    if (!value) {
      return "fa fa-truck";
    }

    if (value.indexOf("fa ") === 0) {
      return value;
    }

    const iconMap = {
      camera: "fa fa-video-camera",
      car: "fa fa-car",
      portable: "fa fa-briefcase",
      "plant-equipment": "fa fa-wrench",
      "prime-mover": "fa fa-truck",
      trailer: "fa fa-truck",
      truck: "fa fa-truck",
      ute: "fa fa-truck",
      van: "fa fa-truck",
    };

    return iconMap[value] || `fa fa-${value}`;
  };

  getAssetColour = (asset) => {
    const colour = String(asset.colour || asset.color || "").trim();
    return /^#[0-9a-fA-F]{6}$/.test(colour) ? colour : "#160f9e";
  };

  getQrReadiness = (asset) => {
    const missing = [];
    if (!asset.qrToken) missing.push("QR token");
    if (!asset.vehicleTypeId) missing.push("vehicle type");
    if (!asset.licenceClass) missing.push("licence class");

    return missing;
  };

  render() {
    const { assets, onOpenQr } = this.props;
    const isSystemAdmin = localStorage.getItem("role") === "SystemAdmin";
    const columnCount = isSystemAdmin ? 13 : 11;

    return (
      <Table className="asset-table" responsive>
        <thead>
          <tr>
            <th>{this.renderSortHeader("Name", "name")}</th>
            <th>{this.renderSortHeader("Friendly Name", "friendlyName")}</th>
            <th>{this.renderSortHeader("Depot", "depotName")}</th>
            {isSystemAdmin && <th>{this.renderSortHeader("Tag 1", "tag1")}</th>}
            {isSystemAdmin && <th>{this.renderSortHeader("Tag 2", "tag2")}</th>}
            <th>{this.renderSortHeader("Customer", "customerName")}</th>
            <th>{this.renderSortHeader("Profile", "profile")}</th>
            <th>QR Setup</th>
            <th>Visual</th>
            <th>Features</th>
            <th>{this.renderSortHeader("Status", "status")}</th>
            <th>{this.renderSortHeader("Updated", "dbUpdated")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {assets.length === 0 && (
            <tr>
              <td className="asset-empty" colSpan={columnCount}>
                No assets match your search.
              </td>
            </tr>
          )}
          {assets.map((asset, index) => {
            const status = (asset.status || "unknown").toLowerCase().replace(/\s+/g, "-");
            const assetColour = this.getAssetColour(asset);
            const qrMissing = this.getQrReadiness(asset);

            return (
              <tr key={asset.id || index}>
                <td><strong>{asset.name}</strong></td>
                <td>{asset.friendlyName || "-"}</td>
                <td>{asset.depotName || "-"}</td>
                {isSystemAdmin && <td>{asset.tag1 || "-"}</td>}
                {isSystemAdmin && <td>{asset.tag2 || "-"}</td>}
                <td>
                  {isSystemAdmin ? (
                    <Link
                      className="asset-customer-link"
                      onClick={() =>
                        localStorage.setItem(
                          "customer",
                          JSON.stringify({
                            customerId: asset.customerId,
                            customerName: asset.customerName,
                          })
                        )
                      }
                      to={`/assets?customerId=${encodeURIComponent(asset.customerId)}&customerName=${encodeURIComponent(asset.customerName)}`}
                    >
                      {asset.customerName}
                    </Link>
                  ) : (
                    <span>{asset.customerName || "-"}</span>
                  )}
                </td>
                <td>{asset.profile || "-"}</td>
                <td>
                  <div className="asset-qr-cell">
                    <span className={`asset-qr-badge ${qrMissing.length ? "needs-setup" : "ready"}`}>
                      <i className={qrMissing.length ? "fa fa-exclamation-circle" : "fa fa-check-circle"}></i>
                      {qrMissing.length ? "Setup" : "Ready"}
                    </span>
                    <button
                      className="asset-qr-open"
                      onClick={() => onOpenQr(asset)}
                      title={asset.qrToken ? "View printable QR code" : "Generate QR token and view printable QR code"}
                      type="button"
                    >
                      <i className="fa fa-qrcode"></i>
                    </button>
                  </div>
                </td>
                <td>
                  <div className="asset-visual-cell">
                    <span className="asset-icon-preview" title={`${asset.icon || "fa fa-truck"} ${assetColour}`}>
                      <i
                        className={this.normalizeIconClass(asset.icon)}
                        style={{ color: assetColour }}
                      ></i>
                    </span>
                  </div>
                </td>
                <td>
                  <div className="asset-feature-list">
                    {this.renderFeature(asset.speakerandMic, "fa fa-microphone", "Speaker and microphone")}
                    {this.renderFeature(asset.sosDuress, "fa fa-life-saver", "SOS duress")}
                    {this.renderFeature(asset.tempA, "fa fa-thermometer-full", "Temperature A")}
                    {this.renderFeature(asset.tempB, "fa fa-thermometer-quarter", "Temperature B")}
                    {this.renderFeature(asset.harshEvents, "fa fa-bolt", "Harsh events")}
                    {this.renderFeature(asset.driverID, "fa fa-id-card-o", "Driver ID")}
                    {this.renderFeature(asset.dashCamera, "fa fa-video-camera", "Dash camera")}
                  </div>
                </td>
                <td>
                  <span className={`asset-status asset-status-${status}`}>
                    {asset.status || "-"}
                  </span>
                </td>
                <td>{asset.dbUpdated ? utcToLocal(asset.dbUpdated) : ""}</td>
                <td className="asset-actions">
                  <Link to={`/editAsset/${asset.id}`}>Edit</Link>
                  <Link to={`/liveAlert/${asset.id}`}>Live Alert</Link>
                  {localStorage.getItem("role") === "SystemAdmin" && (
                    <Link to={`/deleteAsset/${asset.id}`}>Delete</Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }
}

export default AssetTable;
